import { useState } from 'react';
import { useNavigate } from 'react-router';
import PublicLayout from '../components/layout/PublicLayout';
import HomeComposer from '../components/marketing/HomeComposer';
import SubjectCard from '../components/marketing/SubjectCard';
import TeacherCard from '../components/marketing/TeacherCard';
import { SUBJECTS, getSubjectById } from '../data/subjects';
import { TEACHER_DESTINATIONS } from '../data/teacherDestinations';
import { classifySubjectQuestion, isApiError } from '../api';
import { createSubjectMessageNavigationState } from '../lib/subjectNavigation';
import { useAppRole } from '../hooks/useAppRole';
import { canAccessTeacherAccount } from '../lib/teacherSession';

const GENERIC_CLASSIFY_ERROR = 'Vi kunne ikke finde det rette fag lige nu. Prøv igen.';

export default function HomePage() {
  const navigate = useNavigate();
  const { isTeacher, useLiveAuth } = useAppRole();
  const teacherDestinations = TEACHER_DESTINATIONS.filter((destination) => (
    destination.id !== 'prep' || canAccessTeacherAccount(isTeacher, useLiveAuth)
  ));
  const [classifying, setClassifying] = useState(false);
  const [classifyError, setClassifyError] = useState<string | null>(null);

  const handleSubmit = async (message: string) => {
    setClassifying(true);
    setClassifyError(null);

    try {
      const subjectId = await classifySubjectQuestion(message);
      navigate(getSubjectById(subjectId).route, {
        state: createSubjectMessageNavigationState(message),
      });
    } catch (error) {
      setClassifyError(
        isApiError(error) ? error.userMessage : GENERIC_CLASSIFY_ERROR,
      );
      setClassifying(false);
    }
  };

  return (
    <PublicLayout showWaves showFooterCompanyDetails>
      <div
        className="home-hero"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '72px 24px 40px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <h1
          className="home-title"
          style={{
            fontSize: 'clamp(40px, 5vw, 58px)',
            margin: '0 0 16px',
            textAlign: 'center',
            maxWidth: 'min(36ch, 100%)',
            lineHeight: 1.08,
          }}
        >
          GymGPT - AI for gymnasiet
        </h1>
        {classifyError ? (
          <p role="alert" style={{ color: 'var(--status-error)', margin: '0 0 12px' }}>
            {classifyError}
          </p>
        ) : null}
        <HomeComposer onSubmit={handleSubmit} disabled={classifying} />
        <div
          className={isTeacher ? 'teacher-grid' : 'subject-grid'}
          style={{
            display: 'grid',
            gridTemplateColumns: isTeacher
              ? 'repeat(auto-fit, minmax(280px, 1fr))'
              : 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 24,
            marginTop: 56,
            width: isTeacher ? 'min(1068px, 100%)' : 'min(980px, 100%)',
          }}
        >
          {isTeacher
            ? teacherDestinations.map((destination) => (
              <TeacherCard key={destination.id} destination={destination} />
            ))
            : SUBJECTS.map((subject) => (
              <SubjectCard key={subject.id} subject={subject} />
            ))}
        </div>
      </div>
    </PublicLayout>
  );
}
